import { useCallback, useEffect, useState } from 'react';
import { Cabecalho, Layout } from '../componentes/Layout.tsx';
import { Botao, Campo, Selecao } from '../componentes/forma.tsx';
import { Bancada, Busca, Chave, Iniciais, Lista, Painel } from '../componentes/painel.tsx';
import { ErroApi, query } from '../lib/api.ts';
import { useCadastro } from '../lib/cadastro.ts';
import { formatarCnpj, formatarCep, formatarTelefone, soDigitos, UFS } from '../lib/formato.ts';
import { usePode } from '../lib/sessao.tsx';

interface Empresa {
  id: string;
  nome: string;
  cnpj: string;
  inscricaoEstadual: string | null;
  telefone: string | null;
  email: string | null;
  endereco: string | null;
  cidade: string | null;
  uf: string | null;
  cep: string | null;
  relatorioCobranca: boolean;
  ativo: boolean;
}

type Form = Omit<Empresa, 'id'>;

const VAZIO: Form = {
  nome: '', cnpj: '', inscricaoEstadual: '', telefone: '', email: '', endereco: '',
  cidade: '', uf: '', cep: '', relatorioCobranca: false, ativo: true,
};

export function Empresas() {
  const podeEditar = usePode()('pode_empresas');
  const [busca, setBusca] = useState('');
  const [buscaAtiva, setBuscaAtiva] = useState('');
  const [letra, setLetra] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setBuscaAtiva(busca), 260);
    return () => clearTimeout(t);
  }, [busca]);

  const paraFormulario = useCallback((e: Empresa) => {
    const { id: _id, ...resto } = e;
    return resto;
  }, []);

  const c = useCadastro<Empresa, Form>({
    recurso: 'empresas',
    vazio: VAZIO,
    paraFormulario,
    rotulo: (e) => e.nome,
    consulta: query({ busca: buscaAtiva, letra }),
  });


  return (
    <Layout>
      <Cabecalho titulo="Empresas" contagem={c.lista.isPending ? '' : `${c.total}`}>
        {c.editando !== null && (
          <Botao onClick={c.limpar} variante="neutro">
            Nova empresa
          </Botao>
        )}
      </Cabecalho>

      <Bancada>
        <Painel
          titulo={c.editando !== null ? 'Editar empresa' : 'Nova empresa'}
          editando={c.editando !== null}
          erro={c.erroGeral}
          salvo={c.salvo}
          salvando={c.salvando}
          rotuloCriar="Cadastrar empresa"
          aoEnviar={c.enviar}
          aoCancelar={c.limpar}
          somenteLeitura={!podeEditar}
          excluindo={c.excluir.isPending}
          aoExcluir={() => {
            if (confirm(`Excluir ${c.form.nome}?`)) c.excluir.mutate(c.editando!);
          }}
        >
          <Campo
            rotulo="Razão social"
            obrigatorio
            value={c.form.nome}
            erro={c.campos.nome}
            onChange={c.mudar('nome')}
            disabled={!podeEditar}
          />
          <Campo
            rotulo="CNPJ"
            largura="meia"
            obrigatorio
            inputMode="numeric"
            placeholder="00.000.000/0000-00"
            value={formatarCnpj(c.form.cnpj)}
            erro={c.campos.cnpj}
            onChange={c.mudar('cnpj', soDigitos)}
            disabled={!podeEditar}
            dica="É o login da empresa no portal."
          />
          <Campo
            rotulo="Inscrição estadual"
            largura="meia"
            value={c.form.inscricaoEstadual ?? ''}
            erro={c.campos.inscricaoEstadual}
            onChange={c.mudar('inscricaoEstadual')}
            disabled={!podeEditar}
          />

          <Campo
            rotulo="Telefone"
            largura="meia"
            inputMode="numeric"
            value={formatarTelefone(c.form.telefone)}
            erro={c.campos.telefone}
            onChange={c.mudar('telefone', soDigitos)}
            disabled={!podeEditar}
          />
          <Campo
            rotulo="E-mail"
            largura="meia"
            type="email"
            value={c.form.email ?? ''}
            erro={c.campos.email}
            onChange={c.mudar('email')}
            disabled={!podeEditar}
          />

          <Campo
            rotulo="Endereço"
            value={c.form.endereco ?? ''}
            erro={c.campos.endereco}
            onChange={c.mudar('endereco')}
            disabled={!podeEditar}
          />
          <Campo
            rotulo="Cidade"
            largura="meia"
            value={c.form.cidade ?? ''}
            erro={c.campos.cidade}
            onChange={c.mudar('cidade')}
            disabled={!podeEditar}
          />
          <Selecao
            rotulo="UF"
            largura="curta"
            value={c.form.uf ?? ''}
            erro={c.campos.uf}
            onChange={c.mudar('uf')}
            disabled={!podeEditar}
          >
            <option value="">—</option>
            {UFS.map((uf) => (
              <option key={uf} value={uf}>
                {uf}
              </option>
            ))}
          </Selecao>
          <Campo
            rotulo="CEP"
            largura="curta"
            inputMode="numeric"
            value={formatarCep(c.form.cep)}
            erro={c.campos.cep}
            onChange={c.mudar('cep', soDigitos)}
            disabled={!podeEditar}
          />

          <Chave
            rotulo="Entra nos relatórios de cobrança"
            checked={c.form.relatorioCobranca}
            onChange={c.mudar('relatorioCobranca')}
            disabled={!podeEditar}
          />
          <Chave
            rotulo="Ativa"
            descricao="Empresa desativada não aparece no cadastro de exames."
            checked={c.form.ativo}
            onChange={c.mudar('ativo')}
            disabled={!podeEditar}
          />
        </Painel>

        <div>
          <div className="filtros cartao">
            <Busca valor={busca} aoMudar={setBusca} placeholder="Razão social, CNPJ, cidade, telefone" />
            <Iniciais disponiveis={c.iniciais} atual={letra} aoEscolher={setLetra} />
          </div>

          <Lista
            carregando={c.lista.isPending}
            recarregando={c.lista.isFetching}
            erro={c.lista.error instanceof ErroApi ? c.lista.error.message : undefined}
            vazio={
              c.total === 0
                ? {
                    titulo: buscaAtiva || letra ? 'Nenhuma empresa encontrada' : 'Nenhuma empresa cadastrada',
                    apoio:
                      buscaAtiva || letra
                        ? 'Ajuste a busca ou limpe o filtro de inicial.'
                        : 'Cadastre as empresas atendidas em medicina do trabalho.',
                  }
                : undefined
            }
          >
            <table className="tabela">
              <thead>
                <tr>
                  <th>Razão social</th>
                  <th className="tabela--num">CNPJ</th>
                  <th className="tabela--num">Telefone</th>
                  <th>Cidade</th>
                </tr>
              </thead>
              <tbody>
                {c.itens.map((e) => (
                  <tr
                    key={e.id}
                    tabIndex={0}
                    role="button"
                    data-selecionada={c.editando === e.id || undefined}
                    data-inativa={!e.ativo || undefined}
                    onClick={() => c.selecionar(e)}
                    onKeyDown={(ev) => {
                      if (ev.key === 'Enter' || ev.key === ' ') {
                        ev.preventDefault();
                        c.selecionar(e);
                      }
                    }}
                  >
                    <td className="tabela__nome">{e.nome}</td>
                    <td className="tabela--num mono">{formatarCnpj(e.cnpj)}</td>
                    <td className="tabela--num mono">{formatarTelefone(e.telefone) || '—'}</td>
                    <td>{e.cidade ? `${e.cidade}${e.uf ? `/${e.uf}` : ''}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Lista>
        </div>
      </Bancada>
    </Layout>
  );
}
